// Request a move (#2691). The booking owner proposes a new time; the club
// approves it. The sheet quotes the slot for the price difference, shows the
// club's exact reschedule policy, asks the owner to authorise any extra, and
// sends the request. Nothing moves until the club approves.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { isApiError } from "@bw/shared/api";
import {
  MOVE_ERROR,
  alternativesFromErrorDetails,
  newIdempotencyKey,
  type ChangeRequestResponse,
  type MoveSlot,
  type PriceDifference,
} from "@bw/shared/calendar/contract";
import {
  addWallMinutes,
  fromWallClock,
  toWallClock,
  wallMinutesBetween,
} from "@bw/shared/calendar/time";
import { formatMoney } from "@bw/shared/utils";

import { quoteMove, requestMove } from "../hooks/useMoveRequest";

export interface MovableBooking {
  booking_id: string;
  facility_id: string;
  court_id: string;
  start_time: string;
  end_time: string;
}

interface RequestMoveSheetProps {
  open: boolean;
  booking: MovableBooking;
  /** The facility's IANA timezone; inputs are facility wall-clock. */
  timezone: string;
  /** The club's exact policy text and cutoff (null while loading). */
  policyText: string | null;
  /** A proposed slot (e.g. from a calendar drop); defaults to the current one. */
  initialSlot?: { start: Date; end: Date } | null;
  onClose: () => void;
  onSent: (response: ChangeRequestResponse) => void;
}

const STEP_MINUTES = 30;
const LENGTHS = [30, 60, 90, 120, 150, 180, 240];

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function dayInput(wall: Date): string {
  return `${wall.getFullYear()}-${pad(wall.getMonth() + 1)}-${pad(wall.getDate())}`;
}

function timeInput(wall: Date): string {
  return `${pad(wall.getHours())}:${pad(wall.getMinutes())}`;
}

function lengthLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

function money(difference: PriceDifference): string {
  return formatMoney(difference.amount, difference.currency.toUpperCase());
}

function slotLabel(slot: MoveSlot, timezone: string): string {
  try {
    const day = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(new Date(slot.start_time));
    const time = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
    });
    return `${day}, ${time.format(new Date(slot.start_time))} – ${time.format(new Date(slot.end_time))}`;
  } catch {
    return "—";
  }
}

/** Facility wall-clock inputs to a slot, or a reason the inputs are invalid. */
function slotFromInputs(
  day: string,
  time: string,
  length: number,
  courtId: string,
  timezone: string,
): { slot: MoveSlot } | { error: string } {
  const dayMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(time);
  if (!dayMatch || !timeMatch) return { error: "Choose a day and a start time." };
  if (Number(timeMatch[2]) % STEP_MINUTES !== 0) {
    return { error: "Start on the hour or half hour." };
  }
  const wall = new Date(
    Number(dayMatch[1]),
    Number(dayMatch[2]) - 1,
    Number(dayMatch[3]),
    Number(timeMatch[1]),
    Number(timeMatch[2]),
  );
  const start = fromWallClock(wall, timezone);
  const end = fromWallClock(
    // Length is facility wall-clock time, also across a DST change.
    addWallMinutes(wall, length),
    timezone,
  );
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return { error: "Choose a day and a start time." };
  }
  return {
    slot: { start_time: start.toISOString(), end_time: end.toISOString(), court_id: courtId },
  };
}

function requestErrorMessage(error: unknown): string {
  if (!isApiError(error)) {
    return error instanceof Error ? error.message : "The request couldn't be sent.";
  }
  switch (error.code) {
    case MOVE_ERROR.insideCutoff: {
      const hours = error.details?.cutoff_hours;
      return typeof hours === "number"
        ? `Move requests close ${hours} hours before your booking.`
        : "It's too close to your booking to request a move.";
    }
    case MOVE_ERROR.moveNotAllowed:
      return "Choose a start on the hour or half hour, at least 30 minutes long, before your booking starts.";
    case MOVE_ERROR.slotUnavailable:
      return "That time was just taken. Pick another time.";
    case MOVE_ERROR.extraNotAuthorised:
      return "Agree to pay the difference to send this request.";
    case MOVE_ERROR.cardOnFileUnavailable:
      return "Add a saved card to your account so the club can charge the difference.";
    case MOVE_ERROR.paymentPending:
      return "This booking has an unpaid amount. Pay it before requesting a move.";
    case MOVE_ERROR.courtChangeNotAllowed:
      return "The club decides court changes. You can change the time or length.";
    default:
      return error.message || "The request couldn't be sent.";
  }
}

export function RequestMoveSheet({
  open,
  booking,
  timezone,
  policyText,
  initialSlot,
  onClose,
  onSent,
}: RequestMoveSheetProps) {
  const currentStart = new Date(booking.start_time);
  const wallLength = (start: Date, end: Date) =>
    wallMinutesBetween(toWallClock(start, timezone), toWallClock(end, timezone));
  const currentLength = wallLength(currentStart, new Date(booking.end_time));
  const [day, setDay] = useState("");
  const [time, setTime] = useState("");
  const [length, setLength] = useState(60);
  const [authoriseExtra, setAuthoriseExtra] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One idempotency key per request body while the sheet is open: a retry
  // after a timeout or network error replays the same request.
  const requestKeys = useRef(new Map<string, string>());

  useEffect(() => {
    if (!open) return;
    const start = initialSlot?.start ?? currentStart;
    const minutes = initialSlot
      ? wallLength(initialSlot.start, initialSlot.end)
      : currentLength;
    const wall = toWallClock(start, timezone);
    setDay(dayInput(wall));
    setTime(timeInput(wall));
    setLength(minutes > 0 ? minutes : 60);
    setAuthoriseExtra(false);
    setSending(false);
    setError(null);
    requestKeys.current = new Map();
    // Reset only when the sheet opens for a booking or proposed slot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, booking.booking_id, initialSlot]);

  const parsed = useMemo(
    () => slotFromInputs(day, time, length, booking.court_id, timezone),
    [booking.court_id, day, length, time, timezone],
  );
  const slot = "slot" in parsed ? parsed.slot : null;
  const unchanged =
    slot !== null &&
    slot.start_time === new Date(booking.start_time).toISOString() &&
    slot.end_time === new Date(booking.end_time).toISOString();

  const quoteQuery = useQuery({
    queryKey: ["booking", "move-quote", booking.booking_id, slot?.start_time, slot?.end_time],
    queryFn: () => quoteMove(booking.facility_id, booking.booking_id, slot as MoveSlot),
    enabled: open && slot !== null && !unchanged,
    retry: false,
  });
  const quote = quoteQuery.data ?? null;
  const difference = quote?.available ? quote.price_difference : null;
  const needsAuthorisation = difference?.direction === "charge";

  // Consent covers one amount for one slot: a new slot or a new quoted amount
  // needs the player to agree again.
  const consentScope = `${slot?.start_time}|${slot?.end_time}|${difference?.direction}|${difference?.amount}`;
  useEffect(() => {
    setAuthoriseExtra(false);
  }, [consentScope]);
  const lengthOptions = LENGTHS.includes(length)
    ? LENGTHS
    : [...LENGTHS, length].sort((a, b) => a - b);

  const canSend =
    slot !== null &&
    !unchanged &&
    quote?.available === true &&
    (!needsAuthorisation || authoriseExtra) &&
    !sending;

  const send = async () => {
    if (!slot || !canSend) return;
    setSending(true);
    setError(null);
    const body = { ...slot, authorise_extra: needsAuthorisation && authoriseExtra };
    const bodyKey = JSON.stringify(body);
    const idempotencyKey = requestKeys.current.get(bodyKey) ?? newIdempotencyKey();
    requestKeys.current.set(bodyKey, idempotencyKey);
    try {
      const response = await requestMove(booking.facility_id, booking.booking_id, {
        ...body,
        idempotency_key: idempotencyKey,
      });
      requestKeys.current = new Map();
      onSent(response);
    } catch (caught) {
      setError(requestErrorMessage(caught));
      if (isApiError(caught) && caught.code === MOVE_ERROR.slotUnavailable) {
        void quoteQuery.refetch();
      }
      setSending(false);
    }
  };

  const pickAlternative = (alternative: MoveSlot) => {
    const wall = toWallClock(new Date(alternative.start_time), timezone);
    setDay(dayInput(wall));
    setTime(timeInput(wall));
    setLength(
      wallLength(new Date(alternative.start_time), new Date(alternative.end_time)),
    );
  };

  const alternatives =
    quote && !quote.available
      ? (quote.alternatives ?? [])
      : isApiError(quoteQuery.error)
        ? alternativesFromErrorDetails(quoteQuery.error.details)
        : [];

  return (
    <Dialog
      aria-labelledby="request-move-title"
      fullWidth
      maxWidth="xs"
      onClose={sending ? undefined : onClose}
      open={open}
    >
      <DialogTitle id="request-move-title">Request a move</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography color="text.secondary" variant="body2">
            Now: {slotLabel(booking, timezone)}. The club approves every move,
            and your booking stays put until they do.
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <TextField
              label="Day"
              onChange={(event) => setDay(event.target.value)}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ flex: 1 }}
              type="date"
              value={day}
            />
            <TextField
              label="Start"
              onChange={(event) => setTime(event.target.value)}
              size="small"
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: STEP_MINUTES * 60 } }}
              sx={{ flex: 1 }}
              type="time"
              value={time}
            />
          </Stack>
          <TextField
            label="Length"
            onChange={(event) => setLength(Number(event.target.value))}
            select
            size="small"
            value={length}
          >
            {lengthOptions.map((minutes) => (
              <MenuItem key={minutes} value={minutes}>
                {lengthLabel(minutes)}
              </MenuItem>
            ))}
          </TextField>

          {"error" in parsed ? (
            <Typography color="text.secondary" variant="body2">
              {parsed.error}
            </Typography>
          ) : unchanged ? (
            <Typography color="text.secondary" variant="body2">
              Pick a different time to request a move.
            </Typography>
          ) : quoteQuery.isPending ? (
            <Stack direction="row" spacing={1} role="status" sx={{ alignItems: "center" }}>
              <CircularProgress size={16} />
              <Typography color="text.secondary" variant="body2">
                Checking that time…
              </Typography>
            </Stack>
          ) : quote && !quote.available ? (
            <Stack spacing={1}>
              <Alert severity="warning">That time isn&apos;t free.</Alert>
              {alternatives.length > 0 ? (
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
                  {alternatives.map((alternative) => (
                    <Button
                      key={alternative.start_time}
                      onClick={() => pickAlternative(alternative)}
                      size="small"
                      variant="outlined"
                    >
                      {slotLabel(alternative, timezone)}
                    </Button>
                  ))}
                </Stack>
              ) : null}
            </Stack>
          ) : quoteQuery.isError ? (
            <Alert severity="error">{requestErrorMessage(quoteQuery.error)}</Alert>
          ) : difference ? (
            <Box>
              <Typography sx={{ fontWeight: 700 }}>
                {difference.direction === "charge"
                  ? `${money(difference)} more`
                  : difference.direction === "credit"
                    ? `${money(difference)} less`
                    : "No price change"}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {difference.direction === "charge"
                  ? "If the club approves, your saved card is charged the difference."
                  : difference.direction === "credit"
                    ? "If the club approves, the difference is refunded to you."
                    : "The price stays the same."}
              </Typography>
              {needsAuthorisation ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={authoriseExtra}
                      onChange={(event) => setAuthoriseExtra(event.target.checked)}
                    />
                  }
                  label={`I agree to pay ${money(difference)} more if approved`}
                  sx={{ mt: 1 }}
                />
              ) : null}
            </Box>
          ) : null}

          <Paper
            aria-label="Reschedule policy"
            component="section"
            variant="outlined"
            sx={{ p: 1.5, borderRadius: "12px" }}
          >
            <Typography color="text.secondary" variant="body2">
              {policyText ?? "Loading the club's reschedule policy…"}
            </Typography>
          </Paper>

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button disabled={sending} onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!canSend} onClick={() => void send()} variant="contained">
          {sending ? "Sending…" : "Send request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
